import React, { useMemo, useState, useEffect } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  TrendingUp,
} from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { generateUtilisationReport, exportUtilisationToCSV } from '@/utils/utilisationUtils';
import type { UtilisationFilters, UtilisationMetrics, ProjectUtilisation, UtilisationReportData } from '@/types/utilisation';
import { toast } from 'sonner';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmployeeDetailModal } from '@/components/UtilisationReport/EmployeeDetailModal';
import { ProjectDetailModal } from '@/components/UtilisationReport/ProjectDetailModal';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import * as settingsRepository from '@/services/settingsRepository';
import type { UtilisationSettings } from '@/types/timesheetSettings';
import { utilisationApi } from '@/services/utilisationApi';

// New section components
import { KeyPerformanceIndicators } from '@/components/UtilisationReport/KeyPerformanceIndicators';
import { UtilisationOverview } from '@/components/UtilisationReport/UtilisationOverview';
import { PerformanceTrends } from '@/components/UtilisationReport/PerformanceTrends';
import { EmployeePerformanceSection } from '@/components/UtilisationReport/EmployeePerformanceSection';
import { ProjectAllocationSection } from '@/components/UtilisationReport/ProjectAllocationSection';
import { DepartmentSummarySection } from '@/components/UtilisationReport/DepartmentSummarySection';
import { WeeklyBreakdownSection } from '@/components/UtilisationReport/WeeklyBreakdownSection';

// Date range presets - Simplified
const DATE_RANGES = {
  last7days: { label: 'Last 7 Days', days: 7 },
  last30days: { label: 'Last 30 Days', days: 30 },
  last90days: { label: 'Last 90 Days', days: 90 },
  thisMonth: { label: 'This Month', days: 0 },
  lastMonth: { label: 'Last Month', days: 0 },
};

export const UtilisationReportPage: React.FC = () => {
  const { timesheets, employees, projects, currentEmployee } = useTimesheet();
  const { canExportReport, getReportViewAccessLevel } = usePermissions();
  const { mode, isLive } = useScreenDataMode('reports-utilisation');
  
  // Settings state - Load organisation utilisation settings
  const [utilizationSettings, setUtilizationSettings] = useState<UtilisationSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  // API data state - for live mode
  const [apiReportData, setApiReportData] = useState<UtilisationReportData | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Filter state - Simplified
  const [selectedRange, setSelectedRange] = useState<keyof typeof DATE_RANGES>('last30days');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]); // Multi-select departments

  // Modals
  const [selectedEmployeeModal, setSelectedEmployeeModal] = useState<UtilisationMetrics | null>(null);
  const [selectedProjectModal, setSelectedProjectModal] = useState<ProjectUtilisation | null>(null);

  // Section collapse state
  const [, setEmployeeSectionCollapsed] = useState(false);

  // Load organization utilization settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setSettingsLoading(true);
        const orgSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
        setUtilizationSettings(orgSettings.utilisation);
      } catch (error) {
        console.error('Failed to load utilization settings:', error);
        setUtilizationSettings({
          targetUtilisationRate: 0.75,
          calculationMethod: 'billable-total',
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [mode]);

  // Calculate date range - Simplified
  const dateRange = useMemo(() => {
    const today = new Date();
    const range = DATE_RANGES[selectedRange];

    if (selectedRange === 'thisMonth') {
      return {
        start: startOfMonth(today),
        end: endOfMonth(today),
      };
    } else if (selectedRange === 'lastMonth') {
      const lastMonth = subMonths(today, 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth),
      };
    } else {
      return {
        start: subDays(today, range.days),
        end: today,
      };
    }
  }, [selectedRange]);

  // Get unique departments
  const departments = useMemo(() => {
    const depts = new Set(employees.map((e) => e.department));
    return Array.from(depts).sort();
  }, [employees]);

  // Generate filters - Simplified
  const filters: UtilisationFilters = useMemo(
    () => ({
      dateRange,
      departments: selectedDepartments,
      employees: [],
      projects: [],
      includeNonBillable: true,
      groupBy: 'employee',
    }),
    [dateRange, selectedDepartments]
  );

  // Load utilisation data from API when in live mode
  useEffect(() => {
    if (!isLive) {
      setApiReportData(null);
      setApiError(null);
      return;
    }

    const loadApiData = async () => {
      try {
        setApiLoading(true);
        setApiError(null);

        const params = {
          dateFrom: filters.dateRange.start,
          dateTo: filters.dateRange.end,
          employeeIds: filters.employees.length > 0 
            ? filters.employees 
            : (currentEmployee ? [currentEmployee.id] : undefined),
          departments: filters.departments.length > 0 ? filters.departments : undefined,
          projectIds: filters.projects.length > 0 ? filters.projects : undefined,
          includeNonBillable: filters.includeNonBillable,
          groupBy: filters.groupBy,
        };

        const data = await utilisationApi.getUtilisationReport(params);
        setApiReportData(data);
      } catch (error) {
        console.error('Failed to load utilisation data from API:', error);
        setApiError(error instanceof Error ? error.message : 'Failed to load data');
        toast.error('Failed to load utilisation data from API');
      } finally {
        setApiLoading(false);
      }
    };

    loadApiData();
  }, [isLive, filters, currentEmployee]);

  // Filter employees based on access level permission
  const filteredEmployees = useMemo(() => {
    const accessLevel = getReportViewAccessLevel('utilisation');
    
    if (accessLevel === 'own' && currentEmployee) {
      return employees.filter(emp => emp.id === currentEmployee.id);
    } else if (accessLevel === 'team' && currentEmployee) {
      return employees.filter(emp => emp.managerId === currentEmployee.id || emp.id === currentEmployee.id);
    }
    return employees;
  }, [employees, currentEmployee, getReportViewAccessLevel]);

  // Filter timesheets based on access level permission
  const filteredTimesheets = useMemo(() => {
    const accessLevel = getReportViewAccessLevel('utilisation');
    
    if (accessLevel === 'own' && currentEmployee) {
      return timesheets.filter(ts => ts.employeeId === currentEmployee.id);
    } else if (accessLevel === 'team' && currentEmployee) {
      const teamMemberIds = filteredEmployees.map(emp => emp.id);
      return timesheets.filter(ts => teamMemberIds.includes(ts.employeeId));
    }
    return timesheets;
  }, [timesheets, currentEmployee, filteredEmployees, getReportViewAccessLevel]);

  // Generate report data - use API data in live mode, mock data in demo mode
  const reportData = useMemo(() => {
    if (isLive && apiReportData) {
      const accessLevel = getReportViewAccessLevel('utilisation');
      
      if (accessLevel === 'all') {
        return apiReportData;
      }
      
      const allowedEmployeeIds = filteredEmployees.map(emp => emp.id);
      return {
        ...apiReportData,
        employeeMetrics: apiReportData.employeeMetrics.filter(
          metric => allowedEmployeeIds.includes(metric.employeeId)
        ),
      };
    }
    return generateUtilisationReport(filteredTimesheets, filteredEmployees, projects, filters, utilizationSettings || undefined);
  }, [isLive, apiReportData, filteredTimesheets, filteredEmployees, projects, filters, utilizationSettings, getReportViewAccessLevel]);

  // Handle export
  const handleExport = () => {
    try {
      const csv = exportUtilisationToCSV(reportData);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utilisation-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };



  // Handle employee modal
  const handleEmployeeClick = (employee: UtilisationMetrics) => {
    setSelectedEmployeeModal(employee);
  };

  // Handle project modal
  const handleProjectClick = (project: ProjectUtilisation) => {
    setSelectedProjectModal(project);
  };

  // Department options for multi-select
  const departmentOptions = useMemo(() => {
    return departments.map((dept) => ({
      value: dept,
      label: dept,
    }));
  }, [departments]);

  // Handle KPI card clicks
  const handleKPICardClick = (cardType: 'utilisation' | 'hours' | 'billable' | 'peak') => {
    switch (cardType) {
      case 'utilisation':
        // Scroll to employee section and expand it
        setEmployeeSectionCollapsed(false);
        document.getElementById('employee-performance-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      case 'hours':
        // Scroll to employee section
        setEmployeeSectionCollapsed(false);
        document.getElementById('employee-performance-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      case 'billable':
        // Scroll to project section
        document.getElementById('project-allocation-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      case 'peak':
        // Scroll to overview section
        document.getElementById('utilisation-overview-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Utilisation Report"

        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<TrendingUp />}
      />

      {/* Filters - Always Visible */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Date Range Preset */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Date Range</label>
              <Select
                value={selectedRange}
                onValueChange={(value) => {
                  setSelectedRange(value as keyof typeof DATE_RANGES);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DATE_RANGES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {format(dateRange.start, 'MMM d, yyyy')} - {format(dateRange.end, 'MMM d, yyyy')}
              </p>
            </div>

            {/* Department Multi-Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Department</label>
              <MultiSelect
                options={departmentOptions}
                value={selectedDepartments}
                onValueChange={setSelectedDepartments}
                placeholder="All departments"
                emptyMessage="No departments found"
                maxDisplay={2}
              />
              {selectedDepartments.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedDepartments.length} department{selectedDepartments.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Performance Indicators - Always Visible */}
      <KeyPerformanceIndicators 
        summary={reportData.summary} 
        onCardClick={handleKPICardClick}
      />

      {/* Insights */}
      <div className="space-y-4">
        {/* Weekly Trend */}
        <PerformanceTrends trends={reportData.trends} />

        {/* Daily Utilisation & Weekly Breakdown - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div id="utilisation-overview-section">
            <UtilisationOverview 
              dailyUtilisation={reportData.dailyUtilisation}
            />
          </div>
          <WeeklyBreakdownSection
            trends={reportData.trends}
          />
        </div>

        {/* Project Allocation */}
        <div id="project-allocation-section">
          <ProjectAllocationSection
            projectUtilisation={reportData.projectUtilisation}
            onProjectClick={handleProjectClick}
          />
        </div>

        {/* Department Summary & Team Utilisation - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DepartmentSummarySection
            employeeMetrics={reportData.employeeMetrics}
          />
          <div id="employee-performance-section">
            <EmployeePerformanceSection
              employeeMetrics={reportData.employeeMetrics}
              onEmployeeClick={handleEmployeeClick}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedEmployeeModal && (
        <EmployeeDetailModal
          isOpen={!!selectedEmployeeModal}
          onClose={() => setSelectedEmployeeModal(null)}
          employee={selectedEmployeeModal}
          timesheets={timesheets}
          employees={employees}
          projects={projects}
          dateRange={dateRange}
        />
      )}

      {selectedProjectModal && (
        <ProjectDetailModal
          isOpen={!!selectedProjectModal}
          onClose={() => setSelectedProjectModal(null)}
          project={selectedProjectModal}
          timesheets={timesheets}
          employees={employees}
          projects={projects}
          dateRange={dateRange}
        />
      )}
    </div>
  );
};
